import { type Ref, ref, watchEffect } from 'vue'
import { defineStore } from 'pinia'
import { ObjectTypeFunctionalType } from '@/entities/Manuals/ObjectTypes'
import { useTranslate } from '@/shared/lib'
import { useLoadDependenciesCircleObjects } from '@/widgets'

export const useInventObjectCreateModal = defineStore('invent_object_create_modal', () => {
  const { trs } = useTranslate()

  const open = ref(false)
  const pointCoords = ref<number[]>([0, 0])
  const type = ref<ObjectTypeFunctionalType>(ObjectTypeFunctionalType.UNIVERSAL)

  const objectList = ref([
    {
      title: trs('itf.not_defined'),
      value: 0,
    },
  ])

  const coordsList = ref<{ [key: number]: any[] }>({})

  const data = ref<{ dependency: number }>({
    dependency: 0,
  })

  let resolvePromise: ((value: any) => void) | null = null
  let rejectPromise: ((reason?: any) => void) | null = null

  /* Правила валидации */
  const rules = {
    dependency: [
      (value: number) => {
        if (type.value == ObjectTypeFunctionalType.UNIVERSAL) return true

        if (type.value == ObjectTypeFunctionalType.GROUPING && !value)
          return trs('itf.object_create_optional_data')

        return true
      },
    ],
  }

  /**
   * Функция показа модального окна
   */
  const show = (coords: any[]): Promise<boolean> => {
    open.value = true
    pointCoords.value = coords

    return new Promise<boolean>((resolve, reject) => {
      resolvePromise = resolve
      rejectPromise = reject
    })
  }

  /**
   * Функция подтверждения модального окна
   */
  const confirm = () => {
    open.value = false

    if (resolvePromise) {
      const sendData: { coordinates: any[]; group_map_ids: number[] } = {
        coordinates: pointCoords.value,
        group_map_ids: [],
      }

      if (data.value.dependency) {
        sendData.coordinates = [coordsList.value[data.value.dependency]]
        sendData.group_map_ids = [data.value.dependency]
      }

      resolvePromise(sendData)

      clearPromise()
    }
  }

  /**
   * Функция отмены модального окна
   */
  const cancel = () => {
    open.value = false

    if (rejectPromise) {
      rejectPromise()
      clearPromise()
    }
  }

  /**
   * Функция загрузки данных
   * @param objectId
   * @param filter
   */
  const loadData = (objectId: Ref<number>, filter: Ref<any>) => {
    const result = useLoadDependenciesCircleObjects(objectId, filter)

    watchEffect(() => {
      if (result.data.value) {
        objectList.value = [objectList.value[0]]

        result.data.value.data.forEach((item: any) => {
          coordsList.value[item.id] = item.coordinates

          objectList.value.push({
            title: `[${item.object_type.object_name}] ${item.name}`,
            value: item.id,
          })
        })
      }
    })

    return result
  }

  /**
   * Функция очистки promise
   */
  const clearPromise = () => {
    resolvePromise = null
    rejectPromise = null
    pointCoords.value = [0, 0]
    data.value.dependency = 0

    objectList.value = [
      {
        title: trs('itf.not_defined'),
        value: 0,
      },
    ]
  }

  return {
    open,
    data,
    rules,
    type,
    pointCoords,
    objectList,

    show,
    confirm,
    cancel,
    loadData,
  }
})
